"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import Reveal from "../ui/Reveal";

export type DocsLink = {
  /** absolute in-site path, e.g. /docs/wp-adminify/menu-editor/install */
  href: string;
  label: string;
};

export type DocsCategoryCard = {
  /** top-level category slug — also the "All X docs" target */
  slug: string;
  name: string;
  /** total docs in the category, not just the linked ones */
  count: number;
  /** chip colour, cycled by the server so cards stay distinct */
  color: string;
  docs: DocsLink[];
};

type DocsSearchProps = {
  categories: DocsCategoryCard[];
};

const haystack = (category: DocsCategoryCard) =>
  [category.name, ...category.docs.map((doc) => doc.label)].join(" ").toLowerCase();

/** Docs index — design `.dsearch` + `.dgrid`. Filters the server-rendered cards. */
const DocsSearch = ({ categories }: DocsSearchProps) => {
  const [query, setQuery] = useState("");

  const term = query.trim().toLowerCase();

  const visible = useMemo(
    () => (term ? categories.filter((category) => haystack(category).includes(term)) : categories),
    [categories, term]
  );

  return (
    <>
      <div className="jlt-relative jlt-z-[1] jlt-mx-auto jlt-max-w-[560px]">
        <label htmlFor="docs-search" className="jlt-sr-only">
          Search the documentation
        </label>
        <span
          aria-hidden="true"
          className="jlt-pointer-events-none jlt-absolute jlt-left-5 jlt-top-1/2 -jlt-translate-y-1/2 jlt-text-[16px] jlt-opacity-60"
        >
          🔍
        </span>
        <input
          id="docs-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search the docs… e.g. dark mode, white label, folders"
          className="jlt-w-full jlt-rounded-full jlt-border-[1.5px] jlt-border-pl-line2 jlt-bg-white jlt-py-4 jlt-pl-[52px] jlt-pr-[22px] jlt-text-[16px] jlt-text-pl-ink jlt-shadow-pl jlt-outline-none jlt-transition-[border-color,box-shadow] jlt-duration-150 placeholder:jlt-text-pl-mute focus:jlt-border-pl-blue focus:jlt-ring-4 focus:jlt-ring-pl-blue-soft"
        />
      </div>

      {visible.length ? (
        <div className="jlt-mt-[clamp(34px,5vw,54px)] jlt-grid jlt-grid-cols-1 jlt-gap-[18px] sm:jlt-grid-cols-2 lg:jlt-grid-cols-4">
          {visible.map((category, index) => (
            <Reveal key={category.slug} delay={index * 60} className="jlt-h-full">
              <div className="jlt-flex jlt-h-full jlt-flex-col jlt-rounded-[22px] jlt-border jlt-border-pl-line jlt-bg-white jlt-p-[22px] jlt-shadow-pl-sm jlt-transition-[transform,box-shadow] jlt-duration-300 jlt-ease-spring hover:-jlt-translate-y-[6px] hover:jlt-shadow-pl">
                <div className="jlt-mb-[14px] jlt-flex jlt-items-center jlt-gap-3">
                  <span
                    aria-hidden="true"
                    style={{ background: category.color }}
                    className="jlt-h-[30px] jlt-w-[30px] jlt-shrink-0 jlt-rounded-blob3"
                  />
                  <div>
                    <h2 className="jlt-m-0 jlt-font-display jlt-text-[16px] jlt-font-extrabold jlt-leading-[1.15] jlt-text-pl-ink">
                      {category.name}
                    </h2>
                    <span className="jlt-text-[12px] jlt-text-pl-mute">
                      {category.count} article{category.count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                <ul className="jlt-m-0 jlt-grid jlt-flex-1 jlt-list-none jlt-gap-2 jlt-p-0">
                  {category.docs.map((doc) => (
                    <li key={doc.href}>
                      <Link
                        href={doc.href}
                        className="jlt-flex jlt-items-baseline jlt-gap-2 jlt-text-[13.5px] jlt-leading-[1.5] jlt-text-pl-ink2 jlt-transition-colors jlt-duration-150 hover:jlt-text-pl-blue"
                      >
                        <span aria-hidden="true" className="jlt-text-[11px] jlt-opacity-55">
                          📄
                        </span>
                        <span>{doc.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>

                <Link
                  href={`/docs/${category.slug}`}
                  className="jlt-mt-[14px] jlt-border-t jlt-border-pl-line jlt-pt-3 jlt-text-[13px] jlt-font-bold jlt-text-pl-blue hover:jlt-underline"
                >
                  All {category.name} docs →
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      ) : (
        <p
          role="status"
          className="jlt-mx-auto jlt-mt-[clamp(34px,5vw,54px)] jlt-max-w-[520px] jlt-text-center jlt-text-[15px] jlt-leading-[1.62] jlt-text-pl-ink2"
        >
          No docs match “{query.trim()}”. Try a product name, or browse the categories with the
          search box cleared.
        </p>
      )}
    </>
  );
};

export default DocsSearch;
